import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

export interface StatCardProps {
  title: string;
  value: string | number;
  change?: string;
  /** Style for the change line: success (green), warning (amber), neutral (muted) */
  changeVariant?: 'success' | 'warning' | 'neutral';
  icon: React.ReactNode;
  /** @deprecated Kept for callers; icons use theme teal style. */
  gradient?: string;
  isLoading?: boolean;
  /** When true, no outer card (used inside DraggableWidget to avoid card-in-card) */
  embedded?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, changeVariant = 'success', icon, isLoading, embedded }) => {
  const { colors } = useTheme();
  const wrapperClass = embedded
    ? 'min-w-0'
    : `${colors.surface} rounded-2xl shadow-md shadow-black/5 hover:shadow-lg hover:shadow-black/5 transition-shadow duration-200 p-5 border ${colors.border}`;

  const changeClass =
    changeVariant === 'warning'
      ? 'text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 mt-2 flex items-center gap-1 truncate rounded px-1.5 py-0.5'
      : changeVariant === 'neutral'
        ? `text-xs ${colors.textSecondary} mt-2 flex items-center gap-1 truncate`
        : `text-xs ${colors.success} mt-2 flex items-center gap-1 truncate`;

  const showTrendArrow = changeVariant === 'success';

  return (
    <div className={wrapperClass}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${colors.textSecondary} mb-1 truncate`}>{title}</p>
          {isLoading ? (
            <div className={`h-8 w-20 ${colors.surfaceHover} animate-pulse rounded-lg mt-2`}></div>
          ) : (
            <p className={`text-2xl font-bold tracking-tight ${colors.text} truncate`}>{value}</p>
          )}
          {change && !isLoading && (
            <p className={changeClass}>
              {showTrendArrow && (
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              )}
              <span className="truncate">{change}</span>
            </p>
          )}
        </div>
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-teal-200/70 dark:bg-teal-900/30 dark:text-teal-300 dark:ring-teal-800/50">
          <div className="flex h-6 w-6 items-center justify-center">{icon}</div>
        </div>
      </div>
    </div>
  );
};

export default StatCard;

